import React, { useState } from 'react';
import { Box, Typography, Paper, Grid, Button, CircularProgress, Alert, Chip, Divider, TextField, Stack, Pagination } from '@mui/material';
import AttachmentViewer from '../components/AttachmentViewer';
import { enquiryService } from '../api/axios';
import { formatDate } from '../utils/DateUtils';

function AdminEnquiryList({
  isLoading,
  error,
  enquiries,
  onViewDetails,
  onOpenFilePreview,
  refreshEnquiriesList,
  page,
  totalPages,
  onPageChange
}) {
  const [editingItemId, setEditingItemId] = useState(null);
  const [editedItem, setEditedItem] = useState(null);
  const [saveError, setSaveError] = useState('');

  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    setEditedItem({ ...item });
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditedItem(null);
  };

  const handleItemFieldChange = (fieldName, value) => {
    setEditedItem(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSaveItemDetails = async () => {
    try {
        await enquiryService.put(`/enquiry/item/${editedItem.id}`, editedItem);
        setEditingItemId(null);
        setEditedItem(null);
        refreshEnquiriesList();
    } catch (err) {
        setSaveError('Failed to save item details.');
    }
  };

  const getStatusChipColor = (status) => {
    const statusLower = status ? status.toLowerCase() : '';
    if (statusLower.includes('closed won')) return 'success';
    if (statusLower.includes('closed lost')) return 'error';
    if (statusLower.includes('pending')) return 'warning';
    if (statusLower.includes('on hold')) return 'info';
    return 'default';
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }
   if (saveError) {
    return <Alert severity="error" sx={{ m: 2 }}>{saveError}</Alert>;
  }

  if (enquiries.length === 0) {
    return <Typography sx={{ textAlign: 'center', p: 3 }}>No enquiries found for the selected filters.</Typography>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {enquiries.map(enquiry => {
        const enquiryValue = enquiry.items ? enquiry.items.reduce((itemSum, item) => itemSum + (item.quantity || 0) * (item.budgetPerUnit || 0), 0) : 0;

        return (
        <Paper key={enquiry.id} sx={{ p: 2, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Grid container spacing={1} alignItems="flex-start">
            <Grid item xs={12}>
              <Typography variant="h4" component="h3" sx={{ color: 'primary.main', cursor: 'pointer', wordBreak: 'break-all' }} onClick={() => onViewDetails(enquiry)}>
                {enquiry.uniqueId}
              </Typography>
            </Grid>

            {/* Row 1 */}
            <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    <strong>Client:</strong> {enquiry.clientName}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ textAlign: { sm: 'right', md: 'left' } }}>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    <strong>Lead Source:</strong> {enquiry.leadSource || 'N/A'}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ textAlign: { sm: 'right', md: 'left' } }}>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    <strong>Requested By:</strong> {enquiry.requestedBy}
                </Typography>
            </Grid>

            {/* Row 2 - FIX: Removed 'textAlign: right' from Status so it aligns left in the first column */}
            <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body1">
                    <strong>Status:</strong> <Chip label={enquiry.status} color={getStatusChipColor(enquiry.status)} size="small" />
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <Typography variant="enquiryValue">
                    <strong>Enquiry Value:</strong> {enquiryValue.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                {/* FIX: Changed variant from h4 to caption */}
                <Typography variant="body1" display="block">
                    Created: {formatDate(enquiry.createdAt)}
                </Typography>
            </Grid>

            {/* Row 3 - FIX: Removed 'textAlign: right' from Assigned To */}
            <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body1">
                    <strong>Assigned To:</strong> {enquiry.assignedTo || 'N/A'}
                </Typography>
            </Grid>

            {/* New Row Item: Lost Reason */}
            {enquiry.status === 'Closed Lost' && enquiry.lostReason && (
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body1" color="error">
                        <strong>Lost Reason:</strong> {enquiry.lostReason}
                    </Typography>
                </Grid>
            )}

          </Grid>

          <AttachmentViewer attachments={enquiry.imageAttachments} onOpenFilePreview={onOpenFilePreview} />
          <Divider sx={{ my: 2 }} />

          <Typography variant="h5" component="h4" sx={{ mb: 2 }}>Items</Typography>
          <Grid container spacing={2}>
            {enquiry.items && enquiry.items.length > 0 ? (
              enquiry.items.map(item => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Paper sx={{ p: 2, boxShadow: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {editingItemId === item.id ? (
                        <Box>
                            <TextField label="Name" value={editedItem.name} onChange={(e) => handleItemFieldChange('name', e.target.value)} fullWidth size="small" sx={{ mb: 1 }} />
                            <TextField label="Description" value={editedItem.description} onChange={(e) => handleItemFieldChange('description', e.target.value)} fullWidth size="small" multiline rows={2} sx={{ mb: 1 }} />
                            <TextField label="Quantity" value={editedItem.quantity} onChange={(e) => handleItemFieldChange('quantity', e.target.value)} fullWidth size="small" type="number" sx={{ mb: 1 }}/>
                            <TextField label="Budget/Unit" value={editedItem.budgetPerUnit} onChange={(e) => handleItemFieldChange('budgetPerUnit', e.target.value)} fullWidth size="small" type="number" sx={{ mb: 1 }}/>
                            <TextField label="Delivery Location" value={editedItem.deliveryLocation} onChange={(e) => handleItemFieldChange('deliveryLocation', e.target.value)} fullWidth size="small" sx={{ mb: 1 }}/>
                            <TextField label="Target Date" value={editedItem.targetDateOfDelivery} onChange={(e) => handleItemFieldChange('targetDateOfDelivery', e.target.value)} fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} sx={{ mb: 1 }}/>
                        </Box>
                    ) : (
                        <Box>
                          <Typography variant="h6" component="h5" sx={{ fontWeight: 'bold', wordBreak: 'break-all' }}>{item.name}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, wordBreak: 'break-all' }}>
                            {item.description}
                          </Typography>
                          <Typography variant="body2"><strong>Quantity:</strong> {item.quantity || 'N/A'}</Typography>
                          <Typography variant="body2"><strong>Budget Per Unit:</strong> {item.budgetPerUnit ? item.budgetPerUnit.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }) : 'N/A'}</Typography>
                          <Typography variant="body2"><strong>Delivery Location:</strong> {item.deliveryLocation || 'N/A'}</Typography>
                          <Typography variant="caption" display="block">
                            <strong>Target Date of Delivery:</strong> {formatDate(item.targetDateOfDelivery)}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}><strong>Status:</strong> <Chip label={item.status} color={getStatusChipColor(item.status)} size="small" /></Typography>
                        </Box>
                    )}
                    <AttachmentViewer attachments={item.imageAttachments} onOpenFilePreview={onOpenFilePreview} />
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        {editingItemId === item.id ? (
                            <>
                                <Button variant="contained" size="small" onClick={handleSaveItemDetails}>Save</Button>
                                <Button variant="outlined" size="small" onClick={handleCancelEdit}>Cancel</Button>
                            </>
                        ) : (
                             <Button variant="outlined" size="small" onClick={() => handleEditItem(item)}>Edit</Button>
                        )}
                         <Button variant="outlined" size="small" onClick={() => onViewDetails(enquiry, item)}>View Conversation</Button>
                    </Stack>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}><Typography>No items for this enquiry.</Typography></Grid>
            )}
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Button variant="contained" onClick={() => onViewDetails(enquiry)} sx={{ mt: 2 }}>View Overall Conversation</Button>
        </Paper>
        );
      })}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={onPageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
}

export default AdminEnquiryList;